import { Component, inject, input, LOCALE_ID, OnInit, output, signal, WritableSignal, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { AuthService } from '../../../../services/auth-service';
import { UserVTO } from '../../../../services/auth-service.model';
import { debounceTime, distinctUntilChanged, Subject, switchMap, Observable, tap, filter, finalize, map } from 'rxjs';
import { PharmacyService } from '../../../../services/pharmacy/pharmacy.service';
import { Loading } from '../../../../utils/loading/loading';
import { Error } from '../../../public-user-component/auth/shared/error/error';
import { LookupVTO } from '../../../../pages/admin-dashboard/admin-companies/admin-companies.model';
import { PharmacyRequestStatus } from '../../../../pages/admin-dashboard/pharmacies/pharmacies.model';

@Component({
  selector: 'app-assign-employee-dialog',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, Loading, Error],
  templateUrl: './assign-employee-dialog.html',
  styleUrl: './assign-employee-dialog.scss',
})
export class AssignEmployeeDialog implements OnInit {
  private authService = inject(AuthService);
  private pharmacyService = inject(PharmacyService);
  private locale = inject(LOCALE_ID);

  type = input.required<'assign' | 're-assign'>();
  prevEmployeeId = input();
  pharmacyId = input.required<number>();
  nickname = input.required<string>();

  autoCompleteFn = input<(term: string) => Observable<{ list: { id: number; fullName: string }[] }>>();
  submitFn = input<(pharmacyId: number, body: { teleSalesEmployeeId: number; comment?: string }) => Observable<void>>();

  close = output();

  searchTerm = signal<string>('');
  searchResults = signal<UserVTO[]>([]);
  selectedEmployee = signal<{ id: number; fullName: string } | null>(null);
  isLoading = signal(false);
  errorMessage = signal<string | null>(null);

  loadingEmployees = signal<boolean>(false);
  errorEmployees = signal<boolean>(false);
  activeDropDown = signal<boolean>(false);
  employeeResults = signal<{ id: number; fullName: string }[]>([]);

  loadingAssign = signal<boolean>(false);
  errorAssign = signal<string>('');
  employeeAssign = output<{ action: LookupVTO; employee: { id: number; fullName: string } }>();
  toggleDropdown = output();

  context = input<'pharmacy-request' | 'approved-pharmacy'>('pharmacy-request');

  assignForm = new FormGroup({
    employee: new FormControl('', {
      validators: [Validators.required],
    }),
    comment: new FormControl('', {
      validators: [Validators.maxLength(4000)],
    }),
  });

  private setupAutoComplete<T>(
    controlName: string,
    serviceCall: (value: string) => Observable<{ list: T[] }>,
    loading: WritableSignal<boolean>,
    error: WritableSignal<boolean>,
    activeDropDown: WritableSignal<boolean>,
    results: WritableSignal<T[]>,
  ): void {
    this.assignForm
      .get(controlName)!
      .valueChanges.pipe(
        debounceTime(300),
        distinctUntilChanged(),
        tap((value: string | null) => {
          if (!value || value.trim().length < 2) {
            loading.set(false);
            error.set(false);
            activeDropDown.set(false);
            results.set([]);
          }
        }),
        filter((value): value is string => !!value && value.trim().length >= 2),
        tap(() => {
          loading.set(true);
          error.set(false);
          activeDropDown.set(true);
        }),
        switchMap((value: string) => serviceCall(value).pipe(finalize(() => loading.set(false)))),
      )
      .subscribe({
        next: (res: any) => results.set(res.list),
        error: () => error.set(true),
      });
  }

  onSave() {
    if (!this.selectedEmployee()?.id) return;
    if (this.assignForm.get('comment')?.invalid) return;

    this.loadingAssign.set(true);
    this.errorAssign.set('');

    const rawComment = this.assignForm.value.comment?.trim();
    const commentBody = rawComment ? rawComment : undefined;

    let call$: Observable<void>;

    if (this.submitFn()) {
      call$ = this.submitFn()!(this.pharmacyId(), {
        teleSalesEmployeeId: this.selectedEmployee()!.id,
        ...(commentBody && { comment: commentBody }),
      });
    } else if (this.type() === 'assign') {
      call$ = this.pharmacyService.assignEmployee(this.pharmacyId(), {
        employeeId: this.selectedEmployee()!.id,
        ...(commentBody && { comment: commentBody }),
      }) as unknown as Observable<void>;
    } else {
      call$ = this.pharmacyService.reassignEmployee(this.pharmacyId(), {
        employeeId: this.selectedEmployee()!.id,
        ...(commentBody && { comment: commentBody }),
      }) as unknown as Observable<void>;
    }

    call$.pipe(finalize(() => this.loadingAssign.set(false))).subscribe({
      next: () => {
        this.employeeAssign.emit({
          action: {
            id: PharmacyRequestStatus.InProgress,
            titleEn: 'In Progress',
            titleAr: 'قيد المراجعة',
            profileImageFid: '',
          },
          employee: this.selectedEmployee()!,
        });
        this.onCancel();
      },
      error: (err: any) => {
        const backendMsg = this.isArabic() ? err?.error?.messageAr : err?.error?.messageEn;
        const fallbackMsg = this.isArabic() ? 'مشكلة داخلية في السيرفر! من فضلك حاول لاحقا' : 'Internal Server Error';
        this.errorAssign.set(backendMsg || fallbackMsg);
      },
    });
  }

  onCancel() {
    this.close.emit();
  }

  ngOnInit(): void {
    const serviceCall = this.autoCompleteFn()
      ? (value: string) =>
          this.autoCompleteFn()!(value).pipe(
            map((res) => {
              if (this.type() === 're-assign' && this.prevEmployeeId()) {
                return { ...res, list: res.list.filter((e) => e.id !== this.prevEmployeeId()) };
              }
              return res;
            }),
          )
      : (value: string) =>
          this.pharmacyService.pharmacyRequestEmployeeAutoComplete(value).pipe(
            map((res) => {
              if (this.type() === 're-assign' && this.prevEmployeeId()) {
                return { ...res, list: res.list.filter((e) => e.id !== this.prevEmployeeId()) };
              }
              return res;
            }),
          );

    this.setupAutoComplete(
      'employee',
      serviceCall,
      this.loadingEmployees,
      this.errorEmployees,
      this.activeDropDown,
      this.employeeResults,
    );
  }

  onSelectEmployee(item: { id: number; fullName: string }) {
    this.activeDropDown.set(false);
    this.selectedEmployee.set(item);
    this.assignForm.get('employee')!.setValue(item.fullName, { emitEvent: false });
  }

  isArabic = computed(() => this.locale.startsWith('ar'));
  isEnglish = computed(() => this.locale.startsWith('en'));
}
